/**
 * features/auth/components/RequireStoreContext.tsx
 *
 * Fase 1 — Entrada profesional: "Empresa → Tienda → Entrar".
 *
 * Reglas:
 *  - 1 tienda accesible  -> auto-selección, entra directo.
 *  - >1 tienda           -> selector inicial obligatorio en CADA login.
 *  - 0 tiendas           -> pantalla "sin tiendas asignadas" (bloquea operación).
 *  - contexto guardado inválido (tienda ya no accesible) -> fuerza selección.
 *
 * Solo UI/contexto: reutiliza setActiveContext y la data de tiendas accesibles.
 * No toca DB, RLS, permisos ni lógica operativa. El marcador de "confirmado"
 * vive en sessionStorage (se conserva en refresh de la misma sesión, pero se
 * limpia al cerrar sesión → vuelve a preguntar en el próximo login).
 */

import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@features/auth/hooks/useAuth'
import type { BusinessOption, StoreOption } from '@features/users/hooks/useUsers'
import { useStorePickerStores } from '@features/auth/hooks/useStorePickerStores'
import { isCorporateScope } from '@features/organization/utils/accessControl'

function Loader() {
  return (
    <div className="flex h-dvh items-center justify-center bg-surface-50">
      <span className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export function RequireStoreContext({ children }: { children: React.ReactNode }) {
  const { cajero, setActiveContext, logout } = useAuth()
  const { stores: pickerStores, loading } = useStorePickerStores()
  const location = useLocation()
  const navigate = useNavigate()

  // ── Tiendas del SELECTOR OPERATIVO (jerárquico) ──────────────────────────
  // Fuente autoritativa: RPC get_store_picker_stores.
  //   owner → todas · admin/director → tiendas de sus empresas (alcance) ·
  //   operativo → solo user_store_access.
  // El scope alto ya no queda limitado a user_store_access tienda por tienda.
  const groups = useMemo(() => {
    if (!cajero) return [] as { biz: BusinessOption; stores: StoreOption[] }[]
    const byBiz = new Map<string, { biz: BusinessOption; stores: StoreOption[] }>()
    for (const s of pickerStores) {
      if (!byBiz.has(s.business_id)) {
        byBiz.set(s.business_id, {
          biz: { id: s.business_id, brand: s.business_brand, name: s.business_name },
          stores: [],
        })
      }
      byBiz.get(s.business_id)!.stores.push({
        id: s.store_id,
        code: s.store_code,
        name: s.store_name,
        brand: s.business_brand,
        business_id: s.business_id,
      })
    }
    return [...byBiz.values()]
  }, [cajero, pickerStores])

  const accessible = useMemo(() => groups.flatMap(g => g.stores), [groups])
  const only = accessible.length === 1 ? accessible[0] : null
  const activeStoreId = cajero?.activeStoreId ?? ''
  const valid = accessible.some(s => s.id === activeStoreId)
  const confirmKey = cajero ? `celmaxx-store-confirmed:${cajero.id}` : ''

  const apply = (biz: BusinessOption, store: StoreOption) => {
    setActiveContext({
      businessId: biz.id,
      businessBrand: biz.brand,
      businessName: biz.name,
      storeId: store.id,
      storeCode: store.code,
      storeName: store.name || store.brand,
    })
    if (confirmKey) sessionStorage.setItem(confirmKey, store.id)
  }

  // Auto-selección cuando hay exactamente una tienda accesible.
  useEffect(() => {
    if (!cajero || !only) return
    if (cajero.activeStoreId !== only.id) {
      const biz = groups.find(g => g.biz.id === only.business_id)?.biz
      if (biz) apply(biz, only)
    } else if (confirmKey) {
      sessionStorage.setItem(confirmKey, only.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only?.id, cajero?.activeStoreId])

  // Contexto guardado inválido -> limpiar marcador para forzar selección.
  useEffect(() => {
    if (!confirmKey) return
    if (accessible.length > 1 && !valid) sessionStorage.removeItem(confirmKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, accessible.length])

  if (!cajero) return <>{children}</>

  // ── Bypass Modo Corporativo ──────────────────────────────────────────────
  // Las rutas corporativas (/corporate) son SUPERVISIÓN, no operación: no
  // requieren tienda activa. Un usuario con scope corporativo (global/business)
  // entra sin seleccionar tienda. Las rutas operativas siguen exigiendo tienda
  // (la lógica de abajo). Esto NO cambia activeStoreId.
  const canCorporate = isCorporateScope(cajero.accessScope)
  const onCorporateRoute = location.pathname.startsWith('/corporate')
  if (onCorporateRoute && canCorporate) return <>{children}</>

  // Datos aún cargando (evita falso "sin tiendas" mientras llega la lista).
  if (loading) return <Loader />

  // Sin tiendas OPERATIVAS asignadas (ninguna tienda con acceso explícito).
  if (accessible.length === 0) {
    // Usuario con alcance corporativo (global/business) pero sin tienda asignada
    // para operar: no queda "huérfano" en un loader; puede entrar a supervisión.
    if (canCorporate) {
      return (
        <div className="flex h-dvh flex-col items-center justify-center bg-surface-50 px-6 text-center">
          <span className="text-5xl mb-3">🏢</span>
          <p className="text-[15px] font-black text-navy-900">
            Tienes acceso corporativo, pero no tienes tiendas asignadas para operación.
          </p>
          <p className="text-[13px] text-surface-400 mt-1 max-w-sm">
            Puedes entrar a la Vista Corporativa para supervisar, o pedir que te asignen como
            responsable de una tienda en Usuarios y Permisos.
          </p>
          <button
            onClick={() => navigate('/corporate')}
            className="mt-5 px-5 py-2.5 rounded-xl bg-navy-900 text-white text-[13px] font-bold hover:bg-navy-700"
          >
            🌐 Entrar a Vista Corporativa
          </button>
          <button
            onClick={() => void logout()}
            className="mt-3 px-4 py-2 rounded-xl border border-surface-200 text-[13px] font-semibold text-navy-600 hover:bg-surface-100"
          >
            Cerrar sesión
          </button>
        </div>
      )
    }
    // Usuario operativo sin ninguna tienda asignada.
    return (
      <div className="flex h-dvh flex-col items-center justify-center bg-surface-50 px-6 text-center">
        <span className="text-5xl mb-3">🏬</span>
        <p className="text-[15px] font-black text-navy-900">Aún no tienes tiendas asignadas.</p>
        <p className="text-[13px] text-surface-400 mt-1">Pide acceso a tu encargado.</p>
        <button
          onClick={() => void logout()}
          className="mt-5 px-4 py-2 rounded-xl border border-surface-200 text-[13px] font-semibold text-navy-600 hover:bg-surface-100"
        >
          Cerrar sesión
        </button>
      </div>
    )
  }

  // Una sola tienda: se auto-selecciona; entra cuando el contexto ya coincide.
  if (only) {
    return only.id === cajero.activeStoreId ? <>{children}</> : <Loader />
  }

  // Varias tiendas: mostrar selector si el contexto es inválido o no confirmado.
  const confirmed = confirmKey ? sessionStorage.getItem(confirmKey) : null
  const needsChoice = !valid || confirmed !== cajero.activeStoreId
  if (!needsChoice) return <>{children}</>

  return (
    <div className="flex h-dvh flex-col bg-surface-50">
      <div className="bg-navy-900 text-white px-5 py-4 flex-shrink-0">
        <p className="text-[10px] uppercase tracking-widest text-navy-300 font-bold">
          {cajero.activeBusinessBrand ?? cajero.businessBrand ?? 'CELMAXX OS'}
        </p>
        <h1 className="text-[18px] font-black mt-0.5">¿Con qué tienda quieres trabajar hoy?</h1>
        <p className="text-[12px] text-navy-300 mt-1">
          Solo verás información de la tienda seleccionada.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!valid && activeStoreId && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
            <p className="text-[12px] text-amber-800 font-semibold">
              Tu tienda anterior ya no está disponible. Elige una tienda para continuar.
            </p>
          </div>
        )}

        {groups.map(({ biz, stores: bizStores }) => (
          <div key={biz.id}>
            <p className="text-[11px] font-black text-navy-500 uppercase tracking-wide mb-1.5 px-1">
              {biz.brand}
            </p>
            <div className="space-y-1.5">
              {bizStores.map(store => (
                <button
                  key={store.id}
                  onClick={() => apply(biz, store)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-surface-100 text-left hover:border-brand-300 hover:shadow-sm transition-all"
                >
                  <span className="text-[13px] font-mono font-black text-navy-800 w-12 flex-shrink-0">
                    {store.code}
                  </span>
                  <span className="text-[13px] text-navy-700 truncate flex-1">
                    {store.name || store.brand}
                  </span>
                  <span className="text-[11px] font-bold text-brand-600 flex-shrink-0">
                    Entrar →
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-surface-100 bg-white flex-shrink-0 space-y-2">
        {canCorporate && (
          <button
            onClick={() => navigate('/corporate')}
            className="w-full py-2 rounded-xl bg-navy-900 text-white text-[13px] font-bold hover:bg-navy-700"
          >
            🌐 Entrar a Vista Corporativa
          </button>
        )}
        <button
          onClick={() => void logout()}
          className="w-full py-2 rounded-xl border border-surface-200 text-[13px] font-semibold text-navy-600 hover:bg-surface-50"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  )
}
